import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import SEO from "../components/SEO";
import axios from "axios";
import {
  ArrowLeft,
  User,
  Briefcase,
  GraduationCap,
  Heart,
  MapPin,
  Calendar,
  Vote,
  Award,
  Building2,
  Landmark,
  DollarSign,
  ShieldCheck,
  ShieldAlert,
  TrendingUp,
  AlertTriangle,
  Sparkles,
  Shield,
  Gamepad2,
} from "lucide-react";
import CandidatoAvatar from "../components/CandidatoAvatar";

function resolvePhotoUrl(url) {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  const base = (axios.defaults.baseURL || "").replace(/\/api\/?$/, "");
  return `${base}${url}`;
}

function calcAge(dateStr) {
  if (!dateStr) return null;
  const birth = new Date(dateStr);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  try {
    return new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "long", year: "numeric" }).format(new Date(dateStr));
  } catch {
    return dateStr;
  }
}

function capitalize(str) {
  if (!str) return "—";
  return str
    .toLowerCase()
    .replace(/(^|\s)\S/g, (l) => l.toUpperCase())
    .replace(/\(a\)/gi, "(a)");
}

function fmtBRL(n) {
  if (n == null || isNaN(n)) return "—";
  return `R$ ${Number(n).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtBRLShort(n) {
  if (n == null || isNaN(n)) return "—";
  if (n >= 1e9) return `R$ ${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `R$ ${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `R$ ${(n / 1e3).toFixed(0)}K`;
  return fmtBRL(n);
}

function InfoRow({ icon, label, value }) {
  if (!value || value === "#NULO" || value === "NÃO DIVULGÁVEL") return null;
  return (
    <div className="flex items-start gap-3 py-3 border-b border-[var(--border)] last:border-0">
      <div className="p-1.5 rounded-lg bg-[var(--accent-soft)] text-[var(--accent)] mt-0.5">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-[var(--muted)] uppercase tracking-wider">{label}</p>
        <p className="text-sm font-medium text-[var(--text)]">{value}</p>
      </div>
    </div>
  );
}

function IndicadorGauge({ label, value, color, icon, description }) {
  const pct = Math.min(100, Math.max(0, value));
  const radius = 36;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (pct / 100) * circumference;
  return (
    <div className="flex flex-col items-center p-4 rounded-xl border border-[var(--border)] bg-[var(--card)]">
      <div className="relative w-20 h-20 mb-2">
        <svg width="80" height="80" viewBox="0 0 80 80" className="transform -rotate-90">
          <circle cx="40" cy="40" r={radius} fill="none" stroke="var(--border)" strokeWidth="6" />
          <circle cx="40" cy="40" r={radius} fill="none" stroke={color} strokeWidth="6"
            strokeDasharray={circumference} strokeDashoffset={offset}
            strokeLinecap="round" className="transition-all duration-1000" />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-xl font-bold text-[var(--text)]">{Math.round(value)}</span>
        </div>
      </div>
      <div className="flex items-center gap-1 mb-0.5">
        {icon}
        <span className="text-xs font-bold text-[var(--text)]">{label}</span>
      </div>
      <p className="text-[10px] text-[var(--muted)] text-center leading-tight">{description}</p>
    </div>
  );
}

function ResultBadge({ resultado }) {
  if (!resultado) return null;
  const isEleito = resultado.includes("ELEITO");
  const is2turno = resultado === "2º TURNO";
  const isSuplente = resultado === "SUPLENTE";

  let cls = "bg-gray-100 text-gray-600";
  if (isEleito) cls = "bg-green-100 text-green-700";
  else if (is2turno) cls = "bg-amber-100 text-amber-700";
  else if (isSuplente) cls = "bg-blue-100 text-blue-700";
  else cls = "bg-red-100 text-red-600";

  return (
    <span className={`text-xs font-bold px-2 py-1 rounded ${cls}`}>
      {capitalize(resultado)}
    </span>
  );
}

export default function PoliticoDetailPage() {
  const { sqCandidato } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!sqCandidato) return;
    let alive = true;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const res = await axios.get(`/politicos/candidato/${sqCandidato}`);
        if (!alive) return;
        if (res.data?.success) {
          setData(res.data.data);
        } else {
          setError("Candidato não encontrado");
        }
      } catch (err) {
        if (alive) {
          setError(err.response?.status === 404 ? "Candidato não encontrado" : "Erro ao carregar dados");
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => { alive = false; };
  }, [sqCandidato]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto py-12 text-center">
        <div className="inline-block w-8 h-8 border-4 border-[var(--accent)] border-t-transparent rounded-full animate-spin" />
        <p className="mt-4 text-[var(--muted)]">Carregando dados do político...</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="max-w-4xl mx-auto py-12 text-center">
        <p className="text-xl text-red-500 mb-4">{error || "Erro desconhecido"}</p>
        <button onClick={() => navigate(-1)} className="text-[var(--accent)] hover:underline flex items-center gap-2 mx-auto">
          <ArrowLeft size={16} /> Voltar
        </button>
      </div>
    );
  }

  const age = calcAge(data.dataNascimento);
  const latest = data.candidaturas?.[0];

  return (
    <div className="max-w-4xl mx-auto">
      <SEO
        title={`${capitalize(data.nomeUrna || data.nome)} - ${capitalize(latest?.cargo)} (${latest?.uf})`}
        description={`Perfil, patrimônio, prestação de contas e histórico de candidaturas de ${capitalize(data.nomeUrna || data.nome)}, candidato(a) a ${capitalize(latest?.cargo)} por ${latest?.uf}.`}
        path={`/politica/candidato/${sqCandidato}`}
      />
      {/* Back button */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-sm text-[var(--muted)] hover:text-[var(--accent)] transition-colors mb-6"
      >
        <ArrowLeft size={16} /> Voltar
      </button>

      {/* Header Card */}
      <div className="politico-header">
        <div className="politico-header__body">
          {/* Photo */}
          <div className="politico-header__photo">
            <CandidatoAvatar
              candidato={data}
              foto={data.foto}
              nome={data.nomeUrna}
              size={88}
              className="politico-header__img"
            />
          </div>

          {/* Info */}
          <div className="politico-header__info">
            <div className="politico-header__top-row">
              <div className="politico-header__names">
                <h1 className="politico-header__name">{data.nomeUrna}</h1>
                <p className="politico-header__fullname">{capitalize(data.nome)}</p>
              </div>
              {latest && <ResultBadge resultado={latest.resultado} />}
            </div>

            {latest && (
              <div className="politico-header__meta">
                <span className="politico-header__partido">{latest.partido}</span>
                <span className="politico-header__separator" />
                <span className="politico-header__cargo">
                  {capitalize(latest.cargo)}
                </span>
                <span className="politico-header__separator" />
                <span className="politico-header__local">
                  <MapPin size={12} className="inline -mt-0.5 mr-0.5" />
                  {capitalize(latest.municipio)}, {latest.uf}
                </span>
              </div>
            )}

            {/* Quick stats row */}
            <div className="politico-header__stats">
              {age && (
                <div className="politico-header__stat">
                  <Calendar size={13} />
                  <span>{age} anos</span>
                </div>
              )}
              {data.ocupacao && data.ocupacao !== "#NULO" && (
                <div className="politico-header__stat">
                  <Briefcase size={13} />
                  <span>{capitalize(data.ocupacao)}</span>
                </div>
              )}
              {data.grauInstrucao && data.grauInstrucao !== "#NULO" && (
                <div className="politico-header__stat">
                  <GraduationCap size={13} />
                  <span>{capitalize(data.grauInstrucao)}</span>
                </div>
              )}
              {data.candidaturas?.length > 1 && (
                <div className="politico-header__stat">
                  <Vote size={13} />
                  <span>{data.candidaturas.length} candidaturas</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Personal info */}
        <div className="bg-[var(--card)] border border-[var(--border)] rounded-xl p-5">
          <h2 className="text-lg font-bold text-[var(--text)] mb-4">Informações Pessoais</h2>
          <InfoRow icon={<User size={16} />} label="Nome completo" value={capitalize(data.nome)} />
          {data.nomeSocial && data.nomeSocial !== "#NULO" && (
            <InfoRow icon={<User size={16} />} label="Nome social" value={capitalize(data.nomeSocial)} />
          )}
          <InfoRow
            icon={<Calendar size={16} />}
            label="Nascimento"
            value={data.dataNascimento ? `${formatDate(data.dataNascimento)}${age ? ` (${age} anos)` : ""}` : null}
          />
          <InfoRow icon={<MapPin size={16} />} label="Naturalidade" value={data.ufNascimento} />
          <InfoRow icon={<User size={16} />} label="Gênero" value={capitalize(data.genero)} />
          <InfoRow icon={<User size={16} />} label="Cor / Raça" value={capitalize(data.corRaca)} />
          <InfoRow icon={<Heart size={16} />} label="Estado civil" value={capitalize(data.estadoCivil)} />
          <InfoRow icon={<GraduationCap size={16} />} label="Escolaridade" value={capitalize(data.grauInstrucao)} />
          <InfoRow icon={<Briefcase size={16} />} label="Profissão" value={capitalize(data.ocupacao)} />
        </div>

        {/* Latest candidacy details */}
        {latest && (
          <div className="bg-[var(--card)] border border-[var(--border)] rounded-xl p-5">
            <h2 className="text-lg font-bold text-[var(--text)] mb-4">Última Candidatura ({latest.ano})</h2>
            <InfoRow icon={<Vote size={16} />} label="Eleição" value={latest.eleicao} />
            <InfoRow icon={<Award size={16} />} label="Cargo" value={capitalize(latest.cargo)} />
            <InfoRow icon={<Building2 size={16} />} label="Local" value={`${capitalize(latest.municipio)}, ${latest.uf}`} />
            <InfoRow icon={<Vote size={16} />} label="Número" value={latest.numero?.toString()} />
            <InfoRow icon={<Building2 size={16} />} label="Partido" value={`${latest.partido} — ${capitalize(latest.nomePartido)}`} />
            {latest.coligacao && latest.coligacao !== "#NULO" && (
              <InfoRow icon={<Building2 size={16} />} label="Coligação" value={capitalize(latest.coligacao)} />
            )}
            <InfoRow icon={<Award size={16} />} label="Resultado" value={capitalize(latest.resultado)} />
          </div>
        )}
      </div>

      {/* ══════ Ficha Limpa ══════ */}
      <div className="bg-[var(--card)] border border-[var(--border)] rounded-xl p-5 mb-6">
        <div className="flex items-center gap-2 mb-4">
          {data.fichaLimpa?.limpa
            ? <ShieldCheck size={20} className="text-green-500" />
            : <ShieldAlert size={20} className="text-red-500" />}
          <h2 className="text-lg font-bold text-[var(--text)]">Ficha Limpa</h2>
        </div>
        {data.fichaLimpa?.limpa ? (
          <div className="flex items-center gap-3 p-4 rounded-lg bg-green-500/10 border border-green-500/20">
            <ShieldCheck size={24} className="text-green-500 flex-shrink-0" />
            <div>
              <p className="text-sm font-bold text-green-600">Ficha Limpa</p>
              <p className="text-xs text-[var(--muted)]">Nenhuma cassacao ou inaptidao registrada no historico eleitoral deste candidato.</p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {data.fichaLimpa?.cassacoes?.length > 0 && (
              <div>
                <p className="text-xs text-[var(--muted)] uppercase tracking-wider mb-2 flex items-center gap-1">
                  <AlertTriangle size={12} /> Cassacoes / Indeferimentos
                </p>
                <div className="space-y-2">
                  {data.fichaLimpa.cassacoes.map((c, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                      <AlertTriangle size={14} className="text-red-500 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[var(--text)]">{capitalize(c.motivo)}</p>
                        <p className="text-xs text-[var(--muted)]">{c.tipo} · Eleicao {c.ano}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {data.fichaLimpa?.inaptos?.length > 0 && (
              <div>
                <p className="text-xs text-[var(--muted)] uppercase tracking-wider mb-2 flex items-center gap-1">
                  <ShieldAlert size={12} /> Inaptidoes
                </p>
                <div className="space-y-2">
                  {data.fichaLimpa.inaptos.map((c, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                      <ShieldAlert size={14} className="text-amber-500 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-[var(--text)]">{c.situacao}</p>
                        <p className="text-xs text-[var(--muted)]">Eleicao {c.ano}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ══════ Indicadores do Jogo ══════ */}
      {data.indicadores && (
        <div className="bg-[var(--card)] border border-[var(--border)] rounded-xl p-5 mb-6">
          <div className="flex items-center gap-2 mb-1">
            <Gamepad2 size={18} className="text-[var(--accent)]" />
            <h2 className="text-lg font-bold text-[var(--text)]">Indicadores do Jogo</h2>
          </div>
          <p className="text-xs text-[var(--muted)] mb-4">
            Indicadores calculados com dados reais do TSE — Eleição {data.indicadores.ano}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <IndicadorGauge
              label="Carisma" value={data.indicadores.carisma} color="#4ade80"
              icon={<Sparkles size={12} className="text-green-400" />}
              description="Baseado no % de votos obtidos"
            />
            <IndicadorGauge
              label="Influência" value={data.indicadores.influencia} color="#a78bfa"
              icon={<Shield size={12} className="text-purple-400" />}
              description="Baseado no cargo eleito"
            />
            <IndicadorGauge
              label="Recursos" value={data.indicadores.recursos} color="#fbbf24"
              icon={<DollarSign size={12} className="text-amber-400" />}
              description="Baseado em doações de campanha"
            />
            <IndicadorGauge
              label="Risco" value={data.indicadores.risco} color="#f87171"
              icon={<AlertTriangle size={12} className="text-red-400" />}
              description="Patrimônio inexplicado (OLS)"
            />
          </div>
        </div>
      )}

      {/* ══════ Patrimônio + Financiamento ══════ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Patrimônio */}
        <div className="bg-[var(--card)] border border-[var(--border)] rounded-xl p-5">
          <div className="flex items-center gap-2 mb-1">
            <Landmark size={18} className="text-purple-500" />
            <h2 className="text-lg font-bold text-[var(--text)]">Patrimonio Declarado</h2>
          </div>
          <p className="text-xs text-[var(--muted)] mb-4">Eleicao {data.patrimonio?.ano}</p>
          {data.patrimonio?.bens?.length > 0 ? (
            <>
              <div className="flex items-baseline gap-2 mb-4 p-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
                <span className="text-xl font-bold text-purple-500">{fmtBRLShort(data.patrimonio.total)}</span>
                <span className="text-xs text-[var(--muted)]">em {data.patrimonio.bens.length} {data.patrimonio.bens.length === 1 ? 'bem' : 'bens'}</span>
              </div>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {data.patrimonio.bens.map((b, i) => (
                  <div key={i} className="flex items-start justify-between gap-2 py-2 border-b border-[var(--border)] last:border-0">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-[var(--accent)]">{capitalize(b.tipo)}</p>
                      <p className="text-xs text-[var(--muted)] truncate" title={b.descricao}>{capitalize(b.descricao)}</p>
                    </div>
                    <span className="text-xs font-bold text-[var(--text)] whitespace-nowrap">{fmtBRL(b.valor)}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-sm text-[var(--muted)]">Nenhum bem declarado nesta eleicao.</p>
          )}
        </div>

        {/* Financiamento de Campanha */}
        <div className="bg-[var(--card)] border border-[var(--border)] rounded-xl p-5">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign size={18} className="text-green-500" />
            <h2 className="text-lg font-bold text-[var(--text)]">Financiamento de Campanha</h2>
          </div>
          <p className="text-xs text-[var(--muted)] mb-4">Eleicao {data.financiamento?.ano}</p>
          {data.financiamento?.topDoadores?.length > 0 ? (
            <>
              <div className="flex items-baseline gap-2 mb-4 p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                <span className="text-xl font-bold text-green-500">{fmtBRLShort(data.financiamento.total)}</span>
                <span className="text-xs text-[var(--muted)]">em {data.financiamento.doacoes} {data.financiamento.doacoes === 1 ? 'doacao' : 'doacoes'}</span>
              </div>
              <p className="text-xs text-[var(--muted)] uppercase tracking-wider mb-2 flex items-center gap-1">
                <TrendingUp size={12} /> Maiores Doadores
              </p>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {data.financiamento.topDoadores.map((d, i) => {
                  const pct = data.financiamento.total > 0 ? ((d.total / data.financiamento.total) * 100).toFixed(1) : 0;
                  return (
                    <div key={i} className="py-2 border-b border-[var(--border)] last:border-0">
                      <div className="flex items-center justify-between gap-2 mb-0.5">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-[var(--text)] truncate" title={d.nome}>{capitalize(d.nome)}</p>
                          <p className="text-[10px] text-[var(--muted)]">{d.origem} · {d.count} {d.count === 1 ? 'doacao' : 'doacoes'}</p>
                        </div>
                        <span className="text-xs font-bold text-green-500 whitespace-nowrap">{fmtBRL(d.total)}</span>
                      </div>
                      <div className="h-1.5 bg-[var(--border)] rounded-full overflow-hidden">
                        <div className="h-full rounded-full bg-green-500/50 transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <p className="text-sm text-[var(--muted)]">Nenhuma receita de campanha registrada nesta eleicao.</p>
          )}
        </div>
      </div>

      {/* Election history */}
      {data.candidaturas?.length > 1 && (
        <div className="bg-[var(--card)] border border-[var(--border)] rounded-xl p-5 mb-6">
          <h2 className="text-lg font-bold text-[var(--text)] mb-4">
            Histórico Eleitoral ({data.candidaturas.length} candidaturas)
          </h2>
          <div className="space-y-3">
            {data.candidaturas.map((c, i) => {
              const cFoto = resolvePhotoUrl(c.foto);
              return (
                <div
                  key={`${c.ano}-${i}`}
                  className="flex items-center gap-4 p-3 rounded-lg border border-[var(--border)] hover:bg-[var(--border)]/20 transition-colors"
                >
                  {cFoto && (
                    <img
                      src={cFoto}
                      alt={data.nomeUrna}
                      referrerPolicy="no-referrer"
                      className="w-10 h-10 rounded-lg object-cover bg-[var(--border)]"
                      onError={(e) => { e.target.style.display = "none"; }}
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-bold text-[var(--text)]">{c.ano}</span>
                      <span className="text-xs font-semibold text-[var(--accent)]">{c.partido}</span>
                      <span className="text-xs text-[var(--muted)]">Nº {c.numero}</span>
                    </div>
                    <p className="text-sm text-[var(--muted)]">
                      {capitalize(c.cargo)} — {capitalize(c.municipio)}, {c.uf}
                    </p>
                  </div>
                  <ResultBadge resultado={c.resultado} />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Municipality link */}
      {latest && (
        <div className="text-center py-4">
          <Link
            to={`/politica`}
            className="text-[var(--accent)] hover:underline text-sm"
          >
            Ver todos os candidatos
          </Link>
        </div>
      )}
    </div>
  );
}
